/**
 * AeView — Direction A "CRM Operator" UI.
 *
 * Pilot — uses live useAccounts + usePlanVsActual hooks (legacy preserved at
 * AeView.legacy.tsx.bak). KPI strip · Top-deals table · Pipeline-by-stage rail.
 */
import { useMemo, useEffect, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import OperatorKpiCard from "@/components/operator/OperatorKpiCard";
import OperatorTable, { StagePill, HelixBar, OwnerPill, type OperatorColumn } from "@/components/operator/OperatorTable";
import OperatorRightRail, { RailCard, PipelineByStage, ActivityFeed } from "@/components/operator/OperatorRightRail";
import ConversionRateBand from "@/components/dashboards/ConversionRateBand";
import { tokens } from "@/components/operator/tokens";
import { useAccounts, usePlanVsActual, useCheckpointsForAccounts, useAeDailySurface, type CheckpointStatus, type AeCadenceAction } from "./live-data";
import { api } from "@/lib/session";
import { PIPELINE_COVERAGE_MULTIPLIER } from "@shared/schema";
import RepCoachingPanel from "@/components/coaching/RepCoachingPanel";
import { getSession, getCurrentTenant } from "@/lib/session";

// Same shape as ManagerView.helixFor: 10*greens + 5*ambers, capped at 100.
function helixFor(cps: CheckpointStatus[] | undefined): number {
  if (!cps?.length) return 0;
  let pts = 0;
  for (const c of cps) {
    if (c.status === "green") pts += 10;
    else if (c.status === "amber") pts += 5;
  }
  return Math.min(100, Math.round(pts));
}
import { fetchMyActivity } from "@/lib/activity-log";
import { TimingBadge, SoftStopBadge, ReEntryBadge, ExtConsultantBadge, ForecastCategoryPill } from "@/components/dashboards/DealBadges";
import { resolveCategory, blendedForecastPct, type DeclaredTiming } from "@/lib/forecast-rules";
import { OutreachLibrary } from "@/components/marketing/OutreachLibrary";

interface DealRow {
  id: number;
  name: string;
  industry: string;
  stage: string;
  helix: number;          // composite %
  arr: number;
  ownerName: string;
  timing: DeclaredTiming; // S-CP10-4 rep-declared timing
  softStops: number;      // active SOFT_STOP count
  reEntry?: boolean;      // returned from PUSH
  extConsultant?: boolean;
}

// Tenant 2 (Mi6 Demo) sample data only — never shown to real tenants.
const DEMO_DEALS: DealRow[] = [
  { id: 21, name: "Centerline Energy", industry: "Energy",          stage: "Closing",    helix: 84, arr: 420000, ownerName: "Y. Lin", timing: 90, softStops: 0 },
  { id: 22, name: "Helix Chemicals",   industry: "Chemicals & Plastics", stage: "Proposal", helix: 76, arr: 285000, ownerName: "Y. Lin", timing: 70, softStops: 1 },
  { id: 23, name: "Northbeam Building", industry: "Building Products / Construction Materials", stage: "Evaluating", helix: 62, arr: 180000, ownerName: "Y. Lin", timing: 60, softStops: 2, reEntry: true },
  { id: 24, name: "Atlas Manufacturing", industry: "Manufacturing", stage: "Proposal",   helix: 70, arr: 340000, ownerName: "Y. Lin", timing: 80, softStops: 0, extConsultant: true },
  { id: 25, name: "Glassbridge Telecom", industry: "Telecom",       stage: "Stalled",    helix: 41, arr: 95000,  ownerName: "Y. Lin", timing: 50, softStops: 3 },
];

function fmtMoney(n: number): string {
  if (!isFinite(n) || n === 0) return "$0";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
}

// Accepts (and ignores) the legacy { accounts, loading } props that the
// Dashboards.tsx hub still passes through for non-pilot routes.
export default function AeView(_props?: { accounts?: any; loading?: boolean }) {
  const [, setLocation] = useLocation();
  const accountsRes = useAccounts();
  const plan = usePlanVsActual();
  const session = getSession();
  const tenant = getCurrentTenant();
  const isDemo = tenant?.is_demo === true;

  const accountsForCps = useMemo(() => (accountsRes.data ?? []).slice(0, 10).map((a) => a.id), [accountsRes.data]);
  const cpsRes = useCheckpointsForAccounts(accountsForCps);

  // Fetch canonical composite scores from /api/manager/portfolio so the HELIX
  // column matches AccountScorecard. /api/accounts returns stored score (often
  // 0 until a write); the portfolio endpoint computes live. The endpoint is
  // gated to manager/executive roles, so AEs/BDRs fall through to a.score or
  // helixFor() — same as before this patch (no regression).
  const canSeePortfolio = session?.role === "manager" || session?.role === "executive";
  const [compositeMap, setCompositeMap] = useState<Map<number, number>>(new Map());
  useEffect(() => {
    if (!canSeePortfolio) return;
    let cancelled = false;
    (async () => {
      try {
        const portfolio: any = await api("/api/manager/portfolio");
        const accts: any[] = Array.isArray(portfolio)
          ? portfolio
          : Array.isArray(portfolio?.accounts) ? portfolio.accounts : [];
        if (cancelled) return;
        const m = new Map<number, number>();
        for (const a of accts) {
          if (typeof a?.id === "number" && typeof a?.score === "number" && a.score > 0) {
            m.set(a.id, Math.round(a.score));
          }
        }
        setCompositeMap(m);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [canSeePortfolio]);

  // W-LaneR: AE daily surface
  const aeDailyRes = useAeDailySurface();

  const liveRows: DealRow[] = useMemo(() => {
    const accs = accountsRes.data ?? [];
    const cpMap = cpsRes.data ?? {};
    return accs.slice(0, 10).map((a) => {
      const stage = (a.stage || "Prospecting");
      // Map stage strings to Direction A buckets where possible
      const norm =
        /closing|won|commit/i.test(stage) ? "Closing" :
        /propos/i.test(stage) ? "Proposal" :
        /eval|qualif/i.test(stage) ? "Evaluating" :
        /stall|omit/i.test(stage) ? "Stalled" :
        "Prospecting";
      const ext = (a as any).aip_ext ?? {};
      const tRaw = Number(ext.declared_timing);
      const t: DeclaredTiming =
        tRaw === 90 || tRaw === 80 || tRaw === 70 || tRaw === 60 || tRaw === 50 || tRaw === 0
          ? (tRaw as DeclaredTiming)
          : 70;
      // Prefer the canonical composite from /api/manager/portfolio (computed
      // server-side, same source as AccountScorecard). Then the stored a.score.
      // Finally fall back to the legacy CP-rollup helixFor() formula.
      const composite =
        compositeMap.get(a.id) ??
        (typeof a.score === "number" && a.score > 0 ? Math.round(a.score) : helixFor(cpMap[a.id]));
      return {
        id: a.id,
        name: a.name,
        industry: a.industry || "—",
        stage: norm,
        helix: composite,
        arr: a.estArr ?? 0,
        ownerName: session?.name?.split(/\s+/).map((s) => s[0] + ".").join(" ").slice(0, 12) || "Y. Lin",
        timing: t,
        softStops: Number(ext.soft_stop_count ?? 0),
        reEntry: ext.push_re_entry === true,
        extConsultant: ext.ext_consultant === true,
      };
    });
  }, [accountsRes.data, session?.name, cpsRes.data, compositeMap]);

  const rows = liveRows.length > 0 ? liveRows : (isDemo ? DEMO_DEALS : []);
  const usingDemo = liveRows.length === 0 && isDemo;

  // Real plan numbers only — no fake fallbacks. Empty states are honest.
  const quota = Number(plan.data?.annual_quota) || 0;
  const closed = Number(plan.data?.closed_ytd) || 0;
  const weighted = Number(plan.data?.weighted_pipeline) || 0;
  const forecast = closed + weighted;
  const pctClosedToQuota = quota > 0 ? Math.round((closed / quota) * 100) : 0;
  const pctToQuota = quota > 0 ? Math.round((forecast / quota) * 100) : 0;
  const coverageNum = quota > 0 ? forecast / quota : null;
  const coverage = coverageNum !== null ? coverageNum.toFixed(1) : "—";
  const dealsAtRisk = rows.filter((r) => r.stage === "Stalled" || r.helix < 45).length;
  const totalDeals = rows.length;

  // Honest indicator logic — tones reflect real state, not cheerful defaults.
  // Closed YTD: green if ≥ 100% of quota, amber 70-99, red < 70, neutral if no quota set.
  const closedTone: "good" | "warn" | "bad" | "neutral" =
    quota === 0 ? "neutral"
    : pctClosedToQuota >= 100 ? "good"
    : pctClosedToQuota >= 70 ? "warn"
    : "bad";
  const closedDirection: "up" | "down" | "flat" =
    pctClosedToQuota >= 70 ? "up" : (pctClosedToQuota > 0 ? "flat" : "down");
  const closedDelta = quota === 0 ? "No quota set" : `${pctClosedToQuota}% of quota`;

  // Weighted Pipeline: green if forecast covers remaining gap to quota,
  // amber if covers ≥50% of gap, red otherwise, neutral if no quota.
  const gap = Math.max(0, quota - closed);
  const gapCoverage = gap > 0 ? weighted / gap : (weighted > 0 ? 1 : 0);
  const pipelineTone: "good" | "warn" | "bad" | "neutral" =
    quota === 0 ? "neutral"
    : gap === 0 ? "good"
    : gapCoverage >= 1 ? "good"
    : gapCoverage >= 0.5 ? "warn"
    : "bad";
  const pipelineDelta =
    quota === 0 ? "No quota set"
    : gap === 0 ? "Quota reached"
    : gapCoverage >= 1 ? "Forecast covers gap"
    : gapCoverage > 0 ? `Covers ${Math.round(gapCoverage * 100)}% of gap`
    : "No pipeline against gap";
  const pipelineDirection: "up" | "down" | "flat" =
    pipelineTone === "good" ? "up" : pipelineTone === "bad" ? "down" : "flat";

  // Deals at Risk: neutral if no deals loaded, green if zero at risk, red otherwise.
  const riskTone: "good" | "bad" | "neutral" =
    totalDeals === 0 ? "neutral"
    : dealsAtRisk === 0 ? "good"
    : "bad";

  const pipelineRows = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.stage, (counts.get(r.stage) ?? 0) + 1);
    const order = ["Prospecting", "Evaluating", "Proposal", "Closing", "Stalled"];
    return order.map((s) => ({
      stage: s,
      count: counts.get(s) ?? 0,
      color: s === "Closing" ? tokens.color.success : s === "Stalled" ? tokens.color.danger : tokens.color.black,
    }));
  }, [rows]);

  const columns: OperatorColumn<DealRow>[] = [
    {
      key: "name", header: "Account",
      render: (r) => (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600, color: tokens.color.textPrimary, fontSize: 14 }}>{r.name}</span>
            {r.reEntry && <ReEntryBadge />}
            {r.extConsultant && <ExtConsultantBadge />}
          </div>
          <div style={{ fontSize: 12, color: tokens.color.textMuted, marginTop: 2 }}>{r.industry}</div>
        </div>
      ),
    },
    { key: "stage", header: "Stage", render: (r) => <StagePill stage={r.stage} /> },
    { key: "timing", header: "Timing", render: (r) => <TimingBadge timing={r.timing} /> },
    {
      key: "forecast", header: "Forecast",
      render: (r) => (
        <div style={{ display: "inline-flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <ForecastCategoryPill category={resolveCategory(r.helix)} timing={r.timing} />
          <span style={{ fontSize: 12, color: tokens.color.textMuted, fontVariantNumeric: "tabular-nums" }}>
            {blendedForecastPct(r.helix, r.timing)}% blended
          </span>
        </div>
      ),
    },
    { key: "helix", header: "Helix", render: (r) => <HelixBar score={r.helix} /> },
    { key: "softstop", header: "Flags", render: (r) => <SoftStopBadge count={r.softStops} /> },
    { key: "arr", header: "ARR", numeric: true, render: (r) => <span style={{ fontWeight: 600 }}>{fmtMoney(r.arr)}</span> },
    { key: "owner", header: "Owner", render: (r) => <OwnerPill name={r.ownerName} /> },
  ];

  // Demo activity is only shown for the demo tenant (tenant 2).
  const DEMO_ACTIVITY = [
    { text: "Centerline Energy moved to Closing", sub: "You", time: "30 min ago", color: tokens.color.success },
    { text: "Helix Diagnostics CP6 Authority cleared", sub: "You", time: "2 hr ago", color: tokens.color.success },
    { text: "Glassbridge Telecom went silent (Stalled)", sub: "System", time: "Yesterday", color: tokens.color.danger },
    { text: "Atlas Manufacturing sent proposal", sub: "You", time: "Yesterday", color: tokens.color.info },
  ];

  // Live activity_log rows for the current AE. Empty until reps start logging.
  const [liveActivity, setLiveActivity] = useState<{ text: string; sub?: string; time?: string; color?: string }[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchMyActivity(8).then((rows) => {
      if (cancelled) return;
      const mapped = rows.map((r) => {
        const action = String(r.action || "").replace(/_/g, " ");
        const entity = r.entity_type && r.entity_id ? `${r.entity_type} ${r.entity_id}` : "";
        const text = entity ? `${action} — ${entity}` : action;
        const created = new Date(r.created_at);
        const ageMs = Date.now() - created.getTime();
        const mins = Math.round(ageMs / 60000);
        let time = "just now";
        if (mins >= 60 * 24) time = `${Math.round(mins / (60 * 24))}d ago`;
        else if (mins >= 60) time = `${Math.round(mins / 60)}h ago`;
        else if (mins >= 1) time = `${mins}m ago`;
        return { text, sub: "You", time, color: tokens.color.black };
      });
      setLiveActivity(mapped);
    }).catch(() => setLiveActivity([]));
    return () => { cancelled = true; };
  }, []);

  const activity = isDemo && (liveActivity?.length ?? 0) === 0 ? DEMO_ACTIVITY : (liveActivity ?? []);

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Dashboards", href: "/dashboards" },
        { label: "AE" },
      ]}
      primaryAction={{ label: "+ Add Account", href: "/accounts/new" }}
      pageTitle="AE Dashboard"
      pageMeta={`${rows.length} active deals · ${pctToQuota}% to annual quota${usingDemo ? " · sample data" : ""}`}
    >
      <ConversionRateBand scope="self" role="ae" />

      {/* LaneR: Rep coaching panel — top of AE landing */}
      {session?.id && (
        <RepCoachingPanel repUserId={session.id} repRole="ae" />
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <OperatorKpiCard
          label="Closed YTD"
          value={fmtMoney(closed)}
          delta={{ value: closedDelta, direction: closedDirection, tone: closedTone }}
          progress={quota > 0 ? { pct: pctClosedToQuota, color: closedTone === "good" ? tokens.color.success : closedTone === "warn" ? tokens.color.amber : tokens.color.danger } : undefined}
        />
        <OperatorKpiCard
          label="Weighted Pipeline"
          value={fmtMoney(weighted)}
          delta={{ value: pipelineDelta, direction: pipelineDirection, tone: pipelineTone }}
        />
        <OperatorKpiCard
          label="Coverage Multiple"
          value={coverageNum !== null ? `${coverage}x` : "—"}
          delta={{
            value: coverageNum === null ? "No quota set" : `5X coverage suggested`,
            direction: coverageNum === null ? "flat" : (coverageNum >= PIPELINE_COVERAGE_MULTIPLIER ? "up" : "down"),
            tone: coverageNum === null ? "neutral" : (coverageNum >= PIPELINE_COVERAGE_MULTIPLIER ? "good" : "bad")
          }}
        />
        <OperatorKpiCard
          label="Deals at Risk"
          value={totalDeals === 0 ? "—" : String(dealsAtRisk)}
          delta={{
            value: totalDeals === 0 ? "No deals loaded" : "Helix below 45",
            direction: riskTone === "good" ? "up" : riskTone === "bad" ? "down" : "flat",
            tone: riskTone
          }}
        />
      </div>

      {/* LaneR: AE Daily Surface — Today's actions, Overdue, Next steps */}
      <AeDailySections
        aeDailyRes={aeDailyRes}
        accounts={accountsRes.data ?? []}
        onNavigate={setLocation}
      />

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <OperatorTable
            title="Top Deals"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            onRowClick={(r) => setLocation(`/account/${r.id}`)}
            emptyMessage={accountsRes.loading ? "Loading deals..." : "No active deals."}
          />
        </div>
        <OperatorRightRail>
          <RailCard title="Pipeline by Stage">
            <PipelineByStage rows={pipelineRows} />
          </RailCard>
          <RailCard title="Recent Activity">
            {activity.length === 0 ? (
              <div style={{ fontSize: 12.5, color: tokens.color.textMuted, lineHeight: 1.45 }}>
                {liveActivity === null ? "Loading activity..." : "No activity logged yet."}
              </div>
            ) : (
              <ActivityFeed items={activity} />
            )}
          </RailCard>
        </OperatorRightRail>
      </div>
      <div style={{ marginTop: 16 }}>
        <OutreachLibrary />
      </div>
    </OperatorShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LaneR: AE Daily Surface sections — Today's actions, Overdue, Next steps.
// Matches BdrView SectionTitle visual style for consistency.
// ─────────────────────────────────────────────────────────────────────────────

function SectionTitle({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div
      style={{
        fontFamily: tokens.font.display,
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: tokens.color.textMuted,
        margin: "0 0 12px",
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function ActionRow({
  action,
  accountName,
  accountId,
  isOverdue,
  onNavigate,
}: {
  action: AeCadenceAction;
  accountName?: string;
  accountId?: number;
  isOverdue: boolean;
  onNavigate: (path: string) => void;
}) {
  const dueDateStr = action.executed_at
    ? new Date(action.executed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "";
  return (
    <div
      onClick={accountId ? () => onNavigate(`/account/${accountId}`) : undefined}
      role={accountId ? "button" : undefined}
      tabIndex={accountId ? 0 : undefined}
      onKeyDown={accountId ? (e) => e.key === "Enter" && onNavigate(`/account/${accountId}`) : undefined}
      style={{
        padding: "10px 16px",
        borderBottom: `1px solid ${tokens.color.border}`,
        borderLeft: isOverdue ? `2px solid ${tokens.color.danger}` : "2px solid transparent",
        fontSize: 13,
        color: tokens.color.textPrimary,
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: accountId ? "pointer" : "default",
        fontFamily: tokens.font.body,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600,
          color: tokens.color.textPrimary,
          fontSize: 13,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {accountName ?? "Account"}
        </div>
        <div style={{ fontSize: 11, color: tokens.color.textMuted, marginTop: 1 }}>
          {action.action_kind.replace(/_/g, " ")}
          {action.notes ? ` — ${action.notes}` : ""}
        </div>
      </div>
      <span style={{
        fontSize: 11,
        color: isOverdue ? tokens.color.danger : tokens.color.textMuted,
        fontWeight: isOverdue ? 700 : 400,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}>
        {isOverdue ? "Overdue" : dueDateStr}
      </span>
    </div>
  );
}

function NextStepsRow({
  account,
  onNavigate,
}: {
  account: { id: number; name: string; score: number | null; redCps: number[] };
  onNavigate: (path: string) => void;
}) {
  const cpLabel = account.redCps.length > 0
    ? `CP${account.redCps.join(", CP")} needs attention`
    : "No green Checkpoints lit";
  return (
    <div
      onClick={() => onNavigate(`/account/${account.id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onNavigate(`/account/${account.id}`)}
      style={{
        padding: "10px 16px",
        borderBottom: `1px solid ${tokens.color.border}`,
        fontSize: 13,
        color: tokens.color.textPrimary,
        display: "flex",
        alignItems: "center",
        gap: 10,
        cursor: "pointer",
        fontFamily: tokens.font.body,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600,
          color: tokens.color.textPrimary,
          fontSize: 13,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {account.name}
        </div>
        <div style={{ fontSize: 11, color: tokens.color.textMuted, marginTop: 1 }}>{cpLabel}</div>
      </div>
      {account.score !== null && (
        <span style={{
          fontSize: 11,
          fontWeight: 600,
          color: account.score < 45 ? tokens.color.danger : tokens.color.textMuted,
          background: account.score < 45 ? "#FEE2E2" : tokens.color.surfaceMute,
          border: `1px solid ${account.score < 45 ? "#FCA5A5" : tokens.color.border}`,
          borderRadius: 4,
          padding: "2px 7px",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          {account.score}%
        </span>
      )}
    </div>
  );
}

function AeDailySections({
  aeDailyRes,
  accounts,
  onNavigate,
}: {
  aeDailyRes: ReturnType<typeof useAeDailySurface>;
  accounts: import("./live-data").ApiAccount[];
  onNavigate: (path: string) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);

  // Build account lookup (id → name) from the parallel useAccounts hook.
  const accountById = new Map<number, { id: number; name: string; score: number | null }>(
    accounts.map((a) => [a.id, { id: a.id, name: a.name, score: typeof a.score === "number" ? a.score : null }])
  );

  const surface = aeDailyRes.data;

  // Split cadence_actions into today vs overdue
  const todaysActions: AeCadenceAction[] = [];
  const overdueActions: AeCadenceAction[] = [];

  if (surface) {
    for (const action of surface.todays_actions) {
      const dueDate = action.executed_at ? action.executed_at.slice(0, 10) : "";
      if (dueDate < today) {
        overdueActions.push(action);
      } else {
        todaysActions.push(action);
      }
    }
  }

  // Next steps: accounts with red/null CP1-CP5, ranked by composite_score DESC, top 5
  const nextStepAccounts: Array<{ id: number; name: string; score: number | null; redCps: number[] }> = [];
  if (surface) {
    const cpByAccount = new Map<number, Array<{ cp_number: number; status: string }>>();
    for (const a of surface.cp_heatmap) {
      cpByAccount.set(a.id, a.account_cp_status);
    }
    for (const a of surface.cp_heatmap) {
      const cps = cpByAccount.get(a.id) ?? [];
      const redCps = cps
        .filter((c) => c.cp_number <= 5 && (c.status === "red" || c.status === "blocked" || c.status === null))
        .map((c) => c.cp_number)
        .sort((x, y) => x - y);
      const noCriticalGreen = cps.filter((c) => c.cp_number <= 5).every((c) => c.status !== "green");
      if (redCps.length > 0 || noCriticalGreen) {
        const acct = accountById.get(a.id);
        nextStepAccounts.push({
          id: a.id,
          name: a.name,
          score: acct?.score ?? null,
          redCps,
        });
      }
    }
    // Rank by composite_score DESC (highest score = most important to advance)
    nextStepAccounts.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    nextStepAccounts.splice(5); // top 5
  }

  const containerStyle: React.CSSProperties = {
    background: tokens.color.canvas,
    border: `1px solid ${tokens.color.border}`,
    borderRadius: 8,
    marginBottom: 12,
    overflow: "hidden",
  };

  const emptyStyle: React.CSSProperties = {
    padding: "14px 16px",
    fontSize: 13,
    color: tokens.color.textMuted,
    fontFamily: tokens.font.body,
  };

  if (aeDailyRes.loading) {
    return (
      <>
        <SectionTitle style={{ marginTop: 8 }}>Today's actions</SectionTitle>
        <div style={{ ...containerStyle, ...emptyStyle }}>Loading actions...</div>
      </>
    );
  }

  return (
    <>
      {/* Today's actions */}
      <SectionTitle style={{ marginTop: 8 }}>
        {`Today's actions${todaysActions.length > 0 ? ` · ${todaysActions.length}` : ""}`}
      </SectionTitle>
      <div style={containerStyle}>
        {todaysActions.length === 0 ? (
          <div style={emptyStyle}>No actions due today.</div>
        ) : (
          todaysActions.slice(0, 10).map((action) => {
            const acct = action.prospect_id ? accountById.get(action.prospect_id) : undefined;
            return (
              <ActionRow
                key={action.id}
                action={action}
                accountName={acct?.name}
                accountId={acct?.id}
                isOverdue={false}
                onNavigate={onNavigate}
              />
            );
          })
        )}
      </div>

      {/* Overdue */}
      {(overdueActions.length > 0 || !aeDailyRes.loading) && (
        <>
          <SectionTitle style={{ marginTop: 16 }}>
            {`Overdue${overdueActions.length > 0 ? ` · ${overdueActions.length}` : ""}`}
          </SectionTitle>
          <div style={containerStyle}>
            {overdueActions.length === 0 ? (
              <div style={emptyStyle}>No overdue actions.</div>
            ) : (
              overdueActions.slice(0, 10).map((action) => {
                const acct = action.prospect_id ? accountById.get(action.prospect_id) : undefined;
                return (
                  <ActionRow
                    key={action.id}
                    action={action}
                    accountName={acct?.name}
                    accountId={acct?.id}
                    isOverdue={true}
                    onNavigate={onNavigate}
                  />
                );
              })
            )}
          </div>
        </>
      )}

      {/* Next steps across your book */}
      <SectionTitle style={{ marginTop: 16 }}>Next steps across your book</SectionTitle>
      <div style={containerStyle}>
        {nextStepAccounts.length === 0 ? (
          <div style={emptyStyle}>All critical Checkpoints are green across your accounts.</div>
        ) : (
          nextStepAccounts.map((a) => (
            <NextStepsRow key={a.id} account={a} onNavigate={onNavigate} />
          ))
        )}
      </div>
    </>
  );
}
